/**
 * `net.request` capture for the telemetry `net` group.
 *
 * Patches `window.fetch` once (a sentinel guards against double wrapping), and only
 * while the group is on; `stop()` restores the previous `fetch` if it is still ours.
 * For a same-origin `/api/*` request — except `/api/telemetry/*` (the stream's own
 * POSTs) and `/api/events` — it adds an `x-client-req-id` UUID header and records
 * method, route template (UUID, hex and numeric segments become `:id`), status
 * (0 on a network error, `aborted` on an abort), latency, the response
 * `x-request-id` and its size: `content-length`, or — when the header is absent, as
 * on chunked and some compressed responses — the body length read from a clone, the
 * event then waiting until that read ends (null when it fails; an event stream is
 * not read, its size is `stream.*`'s) and, when the request carries `x-intent-id`, the
 * intent it belongs to (`intent_id`). Never a body's content or a query string; no
 * keepalive is added. Other requests pass through untouched.
 *
 * Headers are merged with `new Headers(init?.headers ?? input.headers)`, so a
 * `Request` input keeps its own; the input itself (string, URL or Request) is never
 * converted. The patch wraps whatever `fetch` is installed when it starts, so it
 * composes with a test's `window.fetch` override installed before it.
 *
 * The friction group also starts the patch, to see requests begin (a click that sends
 * one is not dead): every covered request is announced on lib/telemetry/activity.js, but
 * only while `recording()` (the net group) is it tagged and recorded.
 */

import { routeTemplate } from "../lib/telemetry/apiRoutes.js";
import { noteActivity } from "../lib/telemetry/activity.js";
import { isIntentId } from "../lib/telemetry/intentEmitter.js";
import { noteClientReqId } from "../lib/telemetry/streamTrace.js";

export { routeOf, routeTemplate } from "../lib/telemetry/apiRoutes.js";

export const CLIENT_REQ_HEADER = "x-client-req-id";
export const INTENT_HEADER = "x-intent-id";
const PATCHED = Symbol.for("sooth.telemetry.netCapture");

/** A Request from any realm (an iframe's fails `instanceof`): duck-typed. */
const isRequest = (x) =>
  !!x &&
  typeof x === "object" &&
  typeof x.url === "string" &&
  typeof x.method === "string" &&
  typeof x.headers?.get === "function";

function newId() {
  try {
    return crypto.randomUUID();
  } catch {
    return null;
  }
}

function urlOf(input) {
  if (typeof input === "string") return input;
  if (input && typeof input.href === "string") return input.href;
  if (input && typeof input.url === "string") return input.url;
  return null;
}

/** The path of a request this capture covers, or null to pass it through. */
function capturedPath(input, origin) {
  const raw = urlOf(input);
  if (!raw) return null;
  let u;
  try {
    u = new URL(raw, origin);
  } catch {
    return null;
  }
  if (u.origin !== origin) return null;
  const p = u.pathname;
  if (!p.startsWith("/api/") || p.startsWith("/api/telemetry/") || p === "/api/events") return null;
  return p;
}

/**
 * Bytes in `res`'s body, read from a clone so the caller's copy is untouched: 0 with no
 * body, null for an event stream (never ends) or when the read fails.
 */
async function bodyLength(res) {
  let total = 0;
  try {
    if (!res.body) return 0;
    if ((res.headers.get("content-type") || "").includes("text/event-stream")) return null;
    const reader = res.clone().body.getReader();
    for (;;) {
      const { done, value } = await reader.read();
      if (done) return total;
      total += value?.byteLength ?? 0;
    }
  } catch {
    return null;
  }
}

/**
 * Create the capture; `record(event)` receives each `net.request` while `recording()`
 * holds.
 */
export function createNetCapture({
  record,
  recording = () => true,
  win = typeof window !== "undefined" ? window : null,
  now = () => (typeof performance !== "undefined" ? performance.now() : Date.now()),
} = {}) {
  let original = null;
  let patched = null;

  function emit(event) {
    try {
      record(event);
    } catch {
      /* never surface */
    }
  }

  function wrap(inner) {
    const origin = win.location.origin;
    const fn = async function (input, init) {
      const path = capturedPath(input, origin);
      if (!path) return inner.call(this, input, init);
      noteActivity("request");
      const id = recording() ? newId() : null;
      if (!path || !id) return inner.call(this, input, init);
      const headers = new Headers(init?.headers ?? (isRequest(input) ? input.headers : undefined));
      headers.set(CLIENT_REQ_HEADER, id);
      const method = String(
        init?.method || (isRequest(input) && input.method) || "GET",
      ).toUpperCase();
      const base = { event: "net.request", method, route: routeTemplate(path), client_req_id: id };
      // The user intent the request belongs to (lib/apiClient.js, P2 design §4.6).
      const intentId = headers.get(INTENT_HEADER);
      if (isIntentId(intentId)) base.intent_id = intentId.toLowerCase();
      const t0 = now();
      let res;
      try {
        res = await inner.call(this, input, { ...init, headers });
      } catch (e) {
        const aborted = e?.name === "AbortError";
        emit({ ...base, status: 0, aborted, latency_ms: Math.round(now() - t0) });
        throw e;
      }
      try {
        noteClientReqId(res, id);
        const len = Number.parseInt(res.headers.get("content-length") ?? "", 10);
        const done = {
          ...base,
          status: res.status,
          aborted: false,
          latency_ms: Math.round(now() - t0),
          request_id: res.headers.get("x-request-id"),
        };
        if (Number.isSafeInteger(len)) emit({ ...done, bytes: len });
        else bodyLength(res).then((bytes) => emit({ ...done, bytes }));
      } catch {
        /* a response the capture cannot read is still returned */
      }
      return res;
    };
    fn[PATCHED] = true;
    return fn;
  }

  return {
    /** Patch `window.fetch`; idempotent, and a no-op over an already patched fetch. */
    start() {
      if (!win || typeof win.fetch !== "function" || win.fetch[PATCHED]) return;
      original = win.fetch;
      patched = wrap(original);
      win.fetch = patched;
    },
    /** Restore the previous fetch when it is still ours; idempotent. */
    stop() {
      if (!patched) return;
      if (win.fetch === patched) win.fetch = original;
      patched = null;
      original = null;
    },
    get started() {
      return patched !== null;
    },
  };
}
